// Journeys: did the conversation walk the scope's steps, or recite them?
//
// The client scope defines four use cases and, for each, two ordered step lists
// (money lost / fraud attempt). The scope's own words are "clear, simple steps",
// and section 1.2.2 asks for "step by step guidance". So the requirement is not
// only that the right facts appear: it is that they arrive ONE AT A TIME, in
// order, each confirmed before the next, in the language the user is writing in,
// and that the conversation reaches a close (1.2.5 Closure Tracking).
//
// A REPLY THAT DELIVERS FIVE STEPS IN ONE MESSAGE FAILS HERE EVEN WHEN EVERY
// FACT IN IT IS CORRECT. That is the specific complaint the product owner has
// about the current behaviour, and a view that scores it green because the
// content was right would be measuring the wrong thing.
//
// Nothing on this screen is inferred from the reply text. Every check reads a
// field the harness reports, and a field the harness did not report reads "not
// measured", never a pass. A null-tolerant screen is the only kind worth
// building against a contract that does not exist yet.

import { Fragment, useMemo, useState } from "react";

import { ApiError, http, qs } from "../api";
import { Badge, Banner, Empty, useAsync } from "../ui";
import { Card, ErrorBanner, MetricCard, dash, fmtInt, fmtPct, num, shortId } from "./format";
import {
  BRANCH_LABEL,
  JOURNEYS,
  journeyDefinition,
  type JourneyReplay,
  type JourneysResponse,
} from "./journeys";

const JOURNEYS_PATH = "/chatbot/eval-ops/journeys";

/** pass / fail / not measured. There is no fourth state, and no default to pass. */
type Check = "pass" | "fail" | "unmeasured";

interface Verdict {
  delivered: Check;
  order: Check;
  one_at_a_time: Check;
  confirmed: Check;
  language: Check;
  closed: Check;
  overall: Check;
  /** Steps the scope asks for, and how many of them arrived. */
  expected: number;
  arrived: number;
}

const CHECKS: Array<{ key: keyof Omit<Verdict, "overall" | "expected" | "arrived">; label: string; hint: string }> = [
  { key: "delivered", label: "All steps", hint: "Every step the scope lists for this use case and branch was delivered." },
  { key: "order", label: "In order", hint: "The steps arrived in the scope's order. The order is the requirement, not a preference." },
  {
    key: "one_at_a_time",
    label: "One at a time",
    hint: "No single message carried more than one step. Five steps in one message fails this even when every fact is correct.",
  },
  { key: "confirmed", label: "Confirmed", hint: "Each step asked the user to confirm before the next one was sent." },
  { key: "language", label: "User's language", hint: "Every step was delivered in the language the user opened in. No silent switch." },
  { key: "closed", label: "Reached a close", hint: "The conversation reached a close rather than being abandoned mid-ladder." },
];

/**
 * Score one replay against the scope list it claims to be walking.
 *
 * The rule for every check is the same: a FALSE observation fails, a NULL
 * observation is unmeasured, and unmeasured never becomes pass. `overall` is the
 * worst of the six. One fail fails the journey, and one unmeasured check makes
 * the whole verdict unmeasured rather than a pass with a caveat nobody reads.
 */
function scoreJourney(replay: JourneyReplay): Verdict {
  const definition = journeyDefinition(replay.use_case, replay.branch);
  const expected = definition?.steps.length ?? 0;
  const steps = replay.steps ?? [];
  const delivered = steps.filter((s) => s.delivered);
  const arrived = delivered.length;

  const deliveredCheck: Check = !expected || !steps.length ? "unmeasured" : arrived >= expected ? "pass" : "fail";

  // Order: the turn that carried step k must not come before the turn that
  // carried step k-1. Same turn is allowed here and caught by one-at-a-time.
  let order: Check = "pass";
  const ordered = [...delivered].sort((a, b) => a.step_order - b.step_order);
  let previous: number | null = null;
  for (const step of ordered) {
    if (step.turn_index === null || step.turn_index === undefined) {
      order = "unmeasured";
      break;
    }
    if (previous !== null && step.turn_index < previous) {
      order = "fail";
      break;
    }
    previous = step.turn_index;
  }
  if (!delivered.length) order = "unmeasured";

  const packed = delivered.map((s) => s.steps_in_same_message);
  const oneAtATime: Check = !delivered.length
    ? "unmeasured"
    : packed.some((v) => v === null || v === undefined)
      ? "unmeasured"
      : packed.some((v) => num(v) > 1)
        ? "fail"
        : "pass";

  // The last delivered step needs no "shall I go on". There is nothing after it.
  const needsConfirm = ordered.slice(0, Math.max(0, ordered.length - 1));
  const confirmed: Check = !needsConfirm.length
    ? "unmeasured"
    : needsConfirm.some((s) => s.confirmation_asked === null || s.confirmation_asked === undefined)
      ? "unmeasured"
      : needsConfirm.every((s) => s.confirmation_asked === true)
        ? "pass"
        : "fail";

  const userLanguage = String(replay.user_language ?? "").trim();
  const language: Check = !userLanguage || !delivered.length
    ? "unmeasured"
    : delivered.some((s) => !String(s.language ?? "").trim())
      ? "unmeasured"
      : delivered.every((s) => String(s.language).trim() === userLanguage)
        ? "pass"
        : "fail";

  const closed: Check =
    replay.closed === null || replay.closed === undefined ? "unmeasured" : replay.closed ? "pass" : "fail";

  const all = [deliveredCheck, order, oneAtATime, confirmed, language, closed];
  const overall: Check = all.includes("fail") ? "fail" : all.includes("unmeasured") ? "unmeasured" : "pass";

  return { delivered: deliveredCheck, order, one_at_a_time: oneAtATime, confirmed, language, closed, overall, expected, arrived };
}

function CheckCell({ state, title }: { state: Check; title: string }) {
  if (state === "unmeasured") {
    return (
      <span className="muted" title={`${title}: not measured`}>
        —
      </span>
    );
  }
  return (
    <span style={{ color: state === "pass" ? "var(--success)" : "var(--critical)" }} title={title}>
      {state === "pass" ? "ok" : "fail"}
    </span>
  );
}

export function JourneyTab({ runId }: { runId: string }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [reference, setReference] = useState(false);

  // A 404 is not an error to show the operator in red: it is the honest state of
  // a contract nobody has implemented yet. Anything else still surfaces.
  const journeys = useAsync<JourneysResponse | null>(
    () =>
      runId
        ? http
            .get<JourneysResponse>(`${JOURNEYS_PATH}${qs({ run_id: runId, limit: 200 })}`)
            .catch((err: unknown) => {
              if (err instanceof ApiError && err.isNotFound) return null;
              throw err;
            })
        : Promise.resolve(null),
    [runId]
  );

  const replays = journeys.data?.journeys ?? [];
  const scored = useMemo(() => replays.map((r) => ({ replay: r, verdict: scoreJourney(r) })), [replays]);

  const passed = scored.filter((s) => s.verdict.overall === "pass").length;
  const failed = scored.filter((s) => s.verdict.overall === "fail").length;
  const unmeasured = scored.filter((s) => s.verdict.overall === "unmeasured").length;
  const packedFail = scored.filter((s) => s.verdict.one_at_a_time === "fail").length;
  // Null, never 0, when nothing was scorable. The denominator is the replays that
  // could be scored, not every replay: dividing by journeys nobody measured would
  // report a pass rate this run never produced.
  const rate = passed + failed ? passed / (passed + failed) : null;

  const notWired = !journeys.loading && !journeys.error && journeys.data === null;

  return (
    <div className="stack">
      <Card
        title="Journey completion"
        note={
          <>
            One row per replayed conversation, scored against the ordered step list the client scope defines for its
            use case and branch. A journey passes only when every step arrived, in order,{" "}
            <span className="strong">one message per step</span>, each confirmed before the next, in the user&apos;s
            language, and the conversation reached a close. A message carrying five steps fails this even when every
            fact in it is correct.
          </>
        }
        actions={
          <button className="btn sm" onClick={() => setReference((v) => !v)}>
            {reference ? "Hide" : "Show"} the scope&apos;s steps
          </button>
        }
      >
        <ErrorBanner error={journeys.error} what="Failed to load journey replays" />

        {notWired ? (
          <Banner tone="warning">
            Not measured. No harness has posted journey replays for this run. <code>GET {JOURNEYS_PATH}</code> answers
            404. The scope&apos;s step lists are below and the checks are written; the moment the endpoint returns a
            replay, every number here renders. Nothing is shown as 0% in the meantime, because a journey nobody replayed
            is not a journey that failed.
          </Banner>
        ) : (
          <>
            <div className="grid c4">
              <MetricCard
                label="Journeys passed"
                value={fmtPct(rate, 0)}
                sub={`${fmtInt(passed)} of ${fmtInt(passed + failed)} scored replays`}
                sub2={`${fmtInt(unmeasured)} could not be scored`}
                tone={rate === null ? "default" : rate >= 0.9 ? "good" : rate >= 0.5 ? "warn" : "bad"}
                note={passed + failed ? null : "no replay carries enough measurement to be scored"}
              />
              <MetricCard
                label="Replays"
                value={fmtInt(scored.length)}
                sub="conversations the harness replayed against a scope journey"
              />
              <MetricCard
                label="Multi-step messages"
                value={scored.length ? fmtInt(packedFail) : null}
                sub="journeys that packed more than one step into one message"
                sub2="the product owner's complaint, counted"
                tone={packedFail > 0 ? "bad" : "default"}
                note={scored.length ? null : "not measured"}
              />
              <MetricCard
                label="Not scored"
                value={fmtInt(unmeasured)}
                sub="a check the harness did not report"
                sub2="unmeasured is never counted as a pass"
                tone={unmeasured > 0 ? "warn" : "default"}
              />
            </div>

            {!scored.length ? (
              <div style={{ marginTop: "var(--sp-3)" }}>
                <Empty
                  title="No journey replays in this run"
                  body="The endpoint answered, and this run replayed no conversation against a scope journey."
                />
              </div>
            ) : (
              <div className="table-wrap" style={{ marginTop: "var(--sp-3)" }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{ width: 24 }} />
                      <th>Case</th>
                      <th>Use case</th>
                      <th>Branch</th>
                      <th>Lang</th>
                      <th className="num">Steps</th>
                      {CHECKS.map((c) => (
                        <th key={c.key} title={c.hint}>
                          {c.label}
                        </th>
                      ))}
                      <th>Journey</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scored.map(({ replay, verdict }) => {
                      const id = replay.conversation_id;
                      const open = Boolean(expanded[id]);
                      const definition = journeyDefinition(replay.use_case, replay.branch);
                      return (
                        <Fragment key={id}>
                          <tr
                            style={{ cursor: "pointer" }}
                            onClick={() => setExpanded((e) => ({ ...e, [id]: !e[id] }))}
                          >
                            <td className="muted">{open ? "▾" : "▸"}</td>
                            <td className="truncate" style={{ maxWidth: 180 }} title={id}>
                              {dash(replay.case_id) === "—" ? shortId(id, 14) : dash(replay.case_id)}
                            </td>
                            <td className="truncate" style={{ maxWidth: 220 }}>
                              {definition?.label ?? dash(replay.use_case)}
                            </td>
                            <td>{BRANCH_LABEL[replay.branch] ?? dash(replay.branch)}</td>
                            <td>{dash(replay.user_language)}</td>
                            <td className="num">
                              {fmtInt(verdict.arrived)}/{fmtInt(verdict.expected)}
                            </td>
                            {CHECKS.map((c) => (
                              <td key={c.key}>
                                <CheckCell state={verdict[c.key]} title={c.hint} />
                              </td>
                            ))}
                            <td>
                              {verdict.overall === "unmeasured" ? (
                                <span className="muted" title="a check the harness did not report">
                                  not measured
                                </span>
                              ) : (
                                <Badge tone={verdict.overall === "pass" ? "good" : "critical"}>
                                  {verdict.overall === "pass" ? "pass" : "fail"}
                                </Badge>
                              )}
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={7 + CHECKS.length}>
                                {!definition ? (
                                  <Banner tone="warning">
                                    This replay claims use case <code>{dash(replay.use_case)}</code> branch{" "}
                                    <code>{dash(replay.branch)}</code>, which is not one of the eight the scope
                                    defines. It cannot be scored against a step list that does not exist.
                                  </Banner>
                                ) : (
                                  <div className="table-wrap">
                                    <table className="data">
                                      <thead>
                                        <tr>
                                          <th className="num">#</th>
                                          <th>Step the scope asks for</th>
                                          <th>Delivered</th>
                                          <th className="num">Turn</th>
                                          <th className="num">Steps in that message</th>
                                          <th>Asked to confirm</th>
                                          <th>Language</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {definition.steps.map((step) => {
                                          const got = (replay.steps ?? []).find(
                                            (s) => s.step_order === step.order
                                          );
                                          const packedN = got?.steps_in_same_message;
                                          const langOk =
                                            got?.language && replay.user_language
                                              ? String(got.language).trim() ===
                                                String(replay.user_language).trim()
                                              : null;
                                          return (
                                            <tr key={step.order}>
                                              <td className="num">{step.order}</td>
                                              <td>{step.title}</td>
                                              <td
                                                style={{
                                                  color: got?.delivered ? "var(--success)" : "var(--critical)",
                                                }}
                                              >
                                                {got?.delivered ? "yes" : "no"}
                                              </td>
                                              <td className="num">
                                                {got?.turn_index === null || got?.turn_index === undefined
                                                  ? "—"
                                                  : got.turn_index}
                                              </td>
                                              <td
                                                className="num"
                                                style={{
                                                  color: num(packedN) > 1 ? "var(--critical)" : undefined,
                                                }}
                                              >
                                                {packedN === null || packedN === undefined ? "—" : fmtInt(packedN)}
                                              </td>
                                              <td>
                                                {got?.confirmation_asked === null ||
                                                got?.confirmation_asked === undefined ? (
                                                  <span className="muted">—</span>
                                                ) : (
                                                  <span
                                                    style={{
                                                      color: got.confirmation_asked
                                                        ? "var(--success)"
                                                        : "var(--critical)",
                                                    }}
                                                  >
                                                    {got.confirmation_asked ? "yes" : "no"}
                                                  </span>
                                                )}
                                              </td>
                                              <td
                                                style={{
                                                  color:
                                                    langOk === null
                                                      ? undefined
                                                      : langOk
                                                        ? "var(--success)"
                                                        : "var(--critical)",
                                                }}
                                              >
                                                {dash(got?.language)}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                    <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                                      close:{" "}
                                      {replay.closed === null || replay.closed === undefined
                                        ? "not measured"
                                        : replay.closed
                                          ? `reached at turn ${dash(replay.close_turn_index)}`
                                          : "never reached"}{" "}
                                      · {fmtInt(replay.turns)} turns
                                    </p>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      {reference && (
        <Card
          title="The scope's step lists"
          note="Verbatim from section 2.1 of the scope document. Four use cases, each with a money-lost branch and a fraud-attempt branch. This is what 'in order' above is checked against."
        >
          <div className="grid c2">
            {JOURNEYS.map((definition) => (
              <div key={`${definition.use_case}-${definition.branch}`}>
                <h4 className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                  {definition.label} · {BRANCH_LABEL[definition.branch]}
                </h4>
                <ol style={{ fontSize: 12, paddingLeft: "var(--sp-5)", margin: 0 }}>
                  {definition.steps.map((step) => (
                    <li key={step.order}>{step.title}</li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
